import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import { useJspVariables, useAppContext, useAuth, useDebugRender } from '../hooks';
import { IceIcon, PageContainer, PageHeader, UserMenu } from '../components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Unstable_Grid2';

const PageTfaRegistrationDone = () => {
    useDebugRender('PageTfaRegistrationDone');
    const location = useLocation();
    const navigate = useNavigate();
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);

    const { authenticatedUser, logout } = useAuth();
    const userTfaDetails = useSelector(state => state.tfaReducer.userTfaDetails);
    const contactVerified = useSelector(state => state.authenticateReducer.contactVerified);
    const registerAction = useSelector(state => state.tfaReducer.registerAction);

    const isGlobalIdRegistration = userTfaDetails.app === constants.APP_KEY_GLOBAL &&
        !(location?.state?.action === 'TFA_REGISTERED');

    const isRedirectLinkAvailable = JspUtil.isValueAvailable(window.jspRedirectUrl);

    useEffect(() => {
        const isNewRegistration = jspVariables.jspPage === 'REGISTER_GID' || jspVariables.jspPage === 'REGISTER';
        if (!contactVerified && !isNewRegistration) {
            navigate(constants.ROUTE_PAGE_CONFIRM_CONTACT, {
                state: {
                    sourcePage: constants.ROUTE_PAGE_TFA_REGISTRATION_DONE,
                    fromGlobalId: userTfaDetails.app === constants.APP_KEY_GLOBAL,
                },
            });
        }
    }, []);

    const registrationDone = () => {
        if (launcher) {
            logout(userTfaDetails.user, false, false);
            return;
        }
        if (jspVariables.jspPage === 'REGISTER' || jspVariables.jspPage === 'REGISTER_GID'
            || userTfaDetails.app === constants.APP_KEY_GLOBAL
            || registerAction === 'REGISTERED') {
            window.location.href = window.jspRedirectUrl;
            return;
        }
        logout(userTfaDetails.user);
    };

    const getDeliveryOption = () => {
        if (userTfaDetails.isActiveTfaWebAuthn === 'Y') return 'Passkey (Yubikey/Fingerprint)';
        if (userTfaDetails.isActiveTfaMobile === 'Y') return getLabelFromJspVariables('label_authenticator');
        return getLabelFromJspVariables('label_email_notification');
    };

    return (
        <PageContainer id="pageTfaRegistrationDone" dataAutomationId="page_tfa_registration_done">
            <Box sx={panelStyle}>
                <UserMenu
                    authenticatedUser={authenticatedUser}
                    onLogout={() => logout(userTfaDetails.user)}
                    onNavigate={(route) => navigate(route)}
                />
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="tfa_registration_done_header">{isGlobalIdRegistration ? 'Global ID Registration' : getLabelFromJspVariables('title_tfa_registration')}</PageHeader>

                <Typography variant="h6" sx={{ mb: 2 }}>
                    {getLabelFromJspVariables('tfa_registration_complete')}
                </Typography>

                <Grid container spacing={1}>
                    <Grid xs={4} md={2}>
                        <Typography fontWeight={500} id="tfaRegistrationDoneEmail" data-automation-id="tfa_registration_done_email">
                            {isGlobalIdRegistration ? 'Global ID' : getLabelFromJspVariables('label_email')}
                        </Typography>
                    </Grid>
                    <Grid xs={8} md={10}>
                        <Typography id="rd-email" data-automation-id="rd_email">{userTfaDetails.tfaEmail}</Typography>
                    </Grid>

                    <Grid xs={4} md={2}>
                        <Typography fontWeight={500}>{getLabelFromJspVariables('label_delivery_option')}</Typography>
                    </Grid>
                    <Grid xs={8} md={10}>
                        <Typography>{getDeliveryOption()}</Typography>
                    </Grid>
                </Grid>

                {userTfaDetails.isActiveTfaWebAuthn !== 'Y' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" id="complete_nonGlobalId" data-automation-id="complete_non_global_id">{getLabelFromJspVariables('tfa_reg_complete_note')}</Typography>
                        <Link component="button" variant="body2" onClick={() => navigate(constants.ROUTE_PAGE_TFA_REGISTER)}
                            data-automation-id="tfa_registration_done_change_settings_link">
                            {getLabelFromJspVariables('tfa_change_settings')}
                        </Link>
                    </Box>
                )}

                {isRedirectLinkAvailable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button variant="contained" onClick={registrationDone}
                            data-automation-id="tfa_registration_done_return_btn">
                            {getLabelFromJspVariables('action_return_to_application')}
                        </Button>
                    </Box>
                )}
            </Box>
        </PageContainer>
    );
};

export default PageTfaRegistrationDone;
